import {
    ModeEditOutlined as ModeEditOutlinedIcon,
    PencilIcon,
    StickyNote2Outlined as StickyNoteIcon,
} from 'components/icons';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import React, { useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { FeatureFlag } from 'types/features';
import { InvoiceMatterForm, InvoiceTimecard } from 'types/invoice-details';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { InvoiceDetailsSimpleTracker } from '../../InvoiceDetailsChangeTrackers';
import styles from './DescriptionDisplay.module.css';

type Props = {
    isRowDisabled: boolean;
    readonly: boolean;
    invoiceTimecard: InvoiceTimecard;
    index: number;
    control: Control<InvoiceMatterForm>;
    onSubmit: () => void;
    buttonsDisabled: boolean;
    /** The new design's name for the edit button beside the text ("Edit description, {date}, {name}"). */
    editLabel?: string;
};

const ModeEditIcon = ({ className }: { className?: string }) => (
    <ModeEditOutlinedIcon className={cn('text-grey-500', className)} fontSize="small" />
);

// A button so the tooltip opens from the keyboard too; the click is swallowed so reading the note
// does not flip the surrounding cell into description-edit mode. `pointer-events-auto` keeps it
// hoverable inside a read-only row, whose cell turns pointer events off.
function NotesIndicator({ notes }: { notes: string }) {
    return (
        <Tooltip content={notes}>
            <button
                type="button"
                className="text-grey-500 pointer-events-auto flex shrink-0 cursor-default items-center"
                aria-label={t('timecards.form.notes')}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
            >
                <StickyNoteIcon fontSize="small" />
            </button>
        </Tooltip>
    );
}

export const DescriptionDisplay = React.memo(
    ({
        index,
        control,
        onSubmit,
        invoiceTimecard,
        isRowDisabled,
        readonly,
        buttonsDisabled: areButtonsDisabled,
        editLabel,
    }: Props) => {
        const [isEditMode, setIsEditMode] = useState(false);
        const { timecard } = invoiceTimecard;

        const [description] = useWatch({ control, name: [`invoiceTimecards.${index}.description`] });
        const notes = useIsFeatureEnabled(FeatureFlag.TIMECARD_NOTES) ? invoiceTimecard.notes : null;

        return (
            <div className="opacity-decreasing-when-disabled">
                {editLabel && (isRowDisabled || !isEditMode) ? (
                    <p className="text-body1 m-0 mt-1.5 max-w-[72ch] text-pretty wrap-anywhere">
                        {description}
                        {notes ? (
                            <span className="ms-1 inline-flex align-middle">
                                <NotesIndicator notes={notes} />
                            </span>
                        ) : null}
                        {readonly || isRowDisabled ? null : (
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={editLabel}
                                disabled={areButtonsDisabled}
                                onClick={() => setIsEditMode(true)}
                                className="text-foreground-tertiary new:[&_svg[stroke]]:stroke-[1.5] -my-1.5 ms-1 align-middle"
                            >
                                <PencilIcon className="size-[13px]" />
                            </Button>
                        )}
                    </p>
                ) : isRowDisabled || !isEditMode ? (
                    <div
                        className={`${styles.displayRow} ${!readonly && !isRowDisabled ? styles.displayRowClickable : ''} ${readonly || isRowDisabled ? styles.displayRowDisabled : ''}`}
                        role="button"
                        tabIndex={readonly || isRowDisabled ? -1 : 0}
                        onClick={() => (areButtonsDisabled ? undefined : setIsEditMode(true))}
                        onKeyDown={(event) => {
                            if (areButtonsDisabled || readonly || isRowDisabled) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setIsEditMode(true);
                            }
                        }}
                    >
                        <div className={styles.descriptionText}>{description}</div>
                        <span className="flex items-start gap-1">
                            {notes ? <NotesIndicator notes={notes} /> : null}
                            {readonly || isRowDisabled ? null : <ModeEditIcon />}
                        </span>
                    </div>
                ) : (
                    <TextInput
                        control={control}
                        name={`invoiceTimecards.${index}.description`}
                        onBlur={() => {
                            setIsEditMode(false);
                            if (description !== invoiceTimecard.description) {
                                onSubmit();
                            }
                        }}
                        multiline
                        readonly={readonly}
                        autoFocus
                        gap={0}
                        suffixComponent={<ModeEditIcon className="absolute top-[5px] right-[5px] size-5" />}
                    />
                )}
                {timecard?.description && timecard?.description.length > 0 && (
                    <InvoiceDetailsSimpleTracker
                        currentValue={description ? description : undefined}
                        storedValue={timecard?.description}
                    />
                )}
            </div>
        );
    }
);
